import { InfiniteScrollProvider } from '@/infra/InfiniteScrollProvider'
import { type AccountMergeRequest } from './AccountMergeRequest'
import {
  type AccountMergeRequestFilterOption,
  type AccountMergeRequestStore,
} from './AccountMergeRequestStore'
import { action, computed, observable } from 'mobx'
import { toStream } from '@/utils/mobx-util'
import { tap } from 'rxjs'
import type { ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import {
  type AccountMergeRequestDialogs,
  AccountMergeRequestItemViewModel,
  type StatusOrActions,
} from './AccountMergeRequestItemViewModel'
import { match } from '@/utils/pattern-match-util'
import { ViewModelList } from '@/infra/view-model-list'
import { SearchTextViewModel } from '@/infra/SearchTextViewModel'
import type { AlertRetryWrapperFactory } from '@/features/shell'

export class AccountMergeRequestSearchViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  /**
   * Merge Requests.
   */
  private readonly _mergeRequests = new ViewModelList({
    models: () => this.scrollProvider.items.slice(),
    create: (item) => this._makeViewModel(item),
  })

  /**
   * Whether to query for pending or processed account merge requests.
   */
  @observable
  accessor filterOption: AccountMergeRequestFilterOption = 'Pending'

  /**
   * Search text for merge requests, if any.
   */
  readonly searchText = new SearchTextViewModel()

  /**
   * Scroll provider for merge requests.
   */
  readonly scrollProvider = new InfiniteScrollProvider<
    AccountMergeRequest,
    AccountMergeRequestFilterOption
  >({
    limit: 200,
    fetchItems: ({ paging, filter, signal }) => {
      const withRetry = this._createAlertRetryWrapper(
        'Loading merge requests failed',
        signal,
      )
      return withRetry(() =>
        this._store.listMergeRequests({ ...paging, filter }, signal),
      )
    },
  })

  constructor(
    private readonly _store: AccountMergeRequestStore,
    private readonly _createAlertRetryWrapper: AlertRetryWrapperFactory,
    private readonly _dialogs: AccountMergeRequestDialogs,
    private readonly _hubspotPortalId: string,
  ) {
    this._lifecycle.manage(this._mergeRequests)
    this._lifecycle.manage(
      toStream(() => this.filterOption, true).pipe(
        tap(() => void this.refreshItems()),
      ),
    )
  }

  /**
   * Indicates whether the merge request's status or actions menu should be displayed.
   */
  @computed
  get statusOrActions() {
    return match<AccountMergeRequestFilterOption, StatusOrActions>(
      this.filterOption,
      {
        Pending: () => 'Actions',
        Processed: () => 'Status',
      },
    )
  }

  /**
   * Individual merge requests filtered by the given search text (if any).
   */
  @computed
  get mergeRequests() {
    if (!this.searchText.value) {
      return this._mergeRequests.items
    }

    const text = this.searchText.value.toLowerCase()
    return this._mergeRequests.items.filter(
      (mr) =>
        mr.displayName.toLowerCase().includes(text) ||
        mr.email.toLowerCase().includes(text) ||
        mr.primaryAccountId?.toLowerCase().includes(text) ||
        mr.duplicateAccountId?.toLowerCase().includes(text),
    )
  }

  /**
   * Sets the filter option for searching.
   */
  @action.bound
  setFilterOption(filterOption: AccountMergeRequestFilterOption) {
    this.filterOption = filterOption
  }

  /**
   * Refreshes the scroll provider's items.
   */
  @action.bound
  async refreshItems() {
    // The retry alert has reported the failure.
    await this.scrollProvider
      .fetch({ filter: this.filterOption, signal: this._lifecycle.signal })
      .catch(() => undefined)
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
  }

  /**
   * Helper factory for a view model representing a merge request item.
   */
  private _makeViewModel(item: AccountMergeRequest) {
    return new AccountMergeRequestItemViewModel(
      item,
      () => this.statusOrActions,
      this._hubspotPortalId,
      this._dialogs,
    )
  }
}
